import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

import apiRoutes from "../../../constants/api-routes";
import messageLevels from "../../../constants/message-levels";
import messages from "../../../constants/messages";
import navigationItemReferences from "../../../constants/navigation-item-references";
import { getDataGridFakeData } from "../../../helpers/coappDataGridUtils";
import useAuthHeader from "../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../../hooks/useDatagridSorting";
import useToastAlert from "../../../hooks/useToastAlert";
import { selectOrganizationId } from "../../../redux/userSlice";
import CoAppDatagridHeader from "../../global/components/datagrid/CoAppDatagridHeader";
import CoAppStandardDataGrid from "../../global/components/datagrid/CoAppStandardDataGrid";

import { getPluginManagementColumns, pluginColumns } from "./columns/plugin-management-columns";

export default function PluginManagement() {
	const dummyRows = getDataGridFakeData(pluginColumns);
	const { handleToastAlert } = useToastAlert();
	const authHeader = useAuthHeader();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("plugins");
	const { openResourceEditPage, handleOpenResourceHomePage } = useCoAppNavigation();
	const organizationId = useSelector(selectOrganizationId);
	const [plugins, setPlugins] = useState(dummyRows);
	const [loading, setLoading] = useState(true);

	const handlePluginStatusToggleChange = (pluginId, state) => {
		axios.put(apiRoutes.togglePluginStatusForOrganization(organizationId, pluginId), {}, { headers: authHeader })
			.then(() => {
				initPluginManagement();
			})
			.catch((error) => {
				console.log(error);
				handleToastAlert(messageLevels.ERROR, state ? messages.PLUGIN_DISABLED_ERROR_MSG : messages.PLUGIN_ENABLED_ERROR_MSG);
			});
	};

	const columns = useMemo(() => (
		getPluginManagementColumns(
			loading,
			handlePluginStatusToggleChange,
			openResourceEditPage,
			handleOpenResourceHomePage,
			navigationItemReferences
		)
	), [loading, navigationItemReferences]);

	const initPluginManagement = () => {
		axios.get(apiRoutes.getPlatformsAndPlugins(organizationId), { headers: authHeader })
			.then((response) => {
				let platforms = response.data;
				let plugins = [];
				platforms.forEach(platform => {
					if (!platform.plugins) {
						return;
					}
					platform.plugins.forEach(plugin => {
						plugins.push({
							id: plugin.id,
							name: plugin.name,
							groups: plugin.groups.map(group => group),
							active: plugin.active,
							isCustomerManaged: plugin.isCustomerManaged,
							endpoint: plugin.endpoint,
							apiKey: plugin.apiKey,
							platformId: platform.id,
							platformName: platform.name
						});
					});
				});
				setPlugins(plugins);
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
			});

	};

	useEffect(() => {
		initPluginManagement();
		handleInitializeSortModel();
	}, []);

	return (
		<>
			<CoAppDatagridHeader
				title="Plugins"
				addIsPresent={false}
				editIsPresent={false}
				deleteIsPresent={false}
			/>
			<CoAppStandardDataGrid
				columns={columns}
				rows={plugins}
				pinnedColumns={["actions", "name", "active"]}
				allowSelection={false}
				targetResource="plugins"
				loading={loading}
				sortModel={sortModel}
				handleSortModelChange={handleSortModelChange}
			/>
		</>
	);
}